import React, { useState } from 'react';
import {
  Smartphone,
  Tablet,
  Monitor,
  Play,
  CheckCircle2,
  Sparkles,
  Moon,
  Sun,
  Volume2,
} from 'lucide-react';
import { SutraCatalogItem, DailyQuoteItem, ScheduledClass } from '../types';
import { Badge, Button, Tooltip } from '../../components/core';

interface GlobalShowcaseProps {
  sutras: SutraCatalogItem[];
  quotes: DailyQuoteItem[];
  classes: ScheduledClass[];
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const GlobalShowcase: React.FC<GlobalShowcaseProps> = ({ sutras, quotes, classes, addToast }) => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('mobile');
  const [previewRole, setPreviewRole] = useState<'Student' | 'Parent'>('Student');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'sutras' | 'niyam' | 'classes'>('home');
  const [checkedNiyams, setCheckedNiyams] = useState<Record<string, boolean>>({
    'Navkarwali (108 times)': true,
    'Dev Vandan': false,
    'Kandmool Tyag': true,
  });

  const toggleNiyam = (name: string) => {
    setCheckedNiyams((prev) => ({ ...prev, [name]: !prev[name] }));
    addToast('info', 'Student Simulation', `Toggled Niyam: ${name}`);
  };

  const currentQuote = quotes[0] || {
    quote: 'अहिंसा परमो धर्मः - Non-violence is the supreme virtue.',
    author: 'Bhagwan Mahavir',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="border-l-4 border-[var(--gv-brand)] pl-3.5">
          <h1 className="text-2xl font-black tracking-tight text-[var(--gv-ink)]">
            Global Showcase & Mobile Preview
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-xl border border-[var(--gv-border)] bg-white p-1 shadow-xs">
            <button
              type="button"
              onClick={() => setDeviceType('mobile')}
              className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                deviceType === 'mobile' ? 'admin-tab-active' : 'admin-tab-idle'
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" />
              <span>Mobile</span>
            </button>
            <button
              type="button"
              onClick={() => setDeviceType('tablet')}
              className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                deviceType === 'tablet' ? 'admin-tab-active' : 'admin-tab-idle'
              }`}
            >
              <Tablet className="h-3.5 w-3.5" />
              <span>Tablet</span>
            </button>
            <button
              type="button"
              onClick={() => setDeviceType('desktop')}
              className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                deviceType === 'desktop' ? 'admin-tab-active' : 'admin-tab-idle'
              }`}
            >
              <Monitor className="h-3.5 w-3.5" />
              <span>Web Portal</span>
            </button>
          </div>

          <select
            value={previewRole}
            onChange={(e) => setPreviewRole(e.target.value as 'Student' | 'Parent')}
            className="admin-input rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-bold text-[var(--gv-ink)] shadow-xs focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="Student">Student Mobile View</option>
            <option value="Parent">Parent Portal View</option>
          </select>

          <Tooltip content="Toggle Preview Theme">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsDarkMode(!isDarkMode)}
              aria-label="Toggle Preview Theme"
            >
              {isDarkMode ? (
                <Sun className="h-4 w-4 text-amber-500" />
              ) : (
                <Moon className="h-4 w-4 text-indigo-600" />
              )}
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="admin-card flex min-h-[640px] items-center justify-center rounded-2xl bg-stone-100 py-6">
        <div
          className={`flex flex-col overflow-hidden shadow-2xl transition-all duration-300 ${
            deviceType === 'mobile'
              ? 'relative h-[640px] w-[360px] rounded-[36px] border-[10px] border-slate-900 bg-white'
              : deviceType === 'tablet'
              ? 'relative h-[640px] w-[580px] rounded-[28px] border-[10px] border-slate-900 bg-white'
              : 'relative flex h-[600px] w-full max-w-4xl flex-col rounded-2xl border border-stone-300 bg-white'
          }`}
        >
          {deviceType === 'mobile' && (
            <div className="mx-auto flex h-4 w-32 shrink-0 items-center justify-center rounded-b-xl bg-slate-900">
              <div className="h-1 w-10 rounded-full bg-slate-700" />
            </div>
          )}

          <div
            className={`flex shrink-0 items-center justify-between border-b p-4 ${
              isDarkMode
                ? 'border-slate-800 bg-slate-900 text-white'
                : 'border-emerald-900 bg-[var(--gv-brand)] text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 fill-amber-300 text-amber-300" />
              <div>
                <h3 className="text-xs font-black leading-none tracking-tight">Gyan Vatika</h3>
                <span className="font-mono text-[9px] uppercase opacity-80">{previewRole} Experience</span>
              </div>
            </div>

            <Badge variant="soft" className="bg-white/20 font-mono text-[10px] text-white">
              Level 1
            </Badge>
          </div>

          <div
            className={`flex-1 space-y-4 overflow-y-auto p-4 text-xs ${
              isDarkMode ? 'bg-slate-950 text-slate-100' : 'bg-stone-50 text-[var(--gv-ink-muted)]'
            }`}
          >
            {activeTab === 'home' && (
              <>
                <div
                  className={`space-y-1.5 rounded-2xl border p-4 shadow-xs ${
                    isDarkMode
                      ? 'border-slate-800 bg-slate-900'
                      : 'border-emerald-200/80 bg-gradient-to-r from-emerald-50 to-amber-50'
                  }`}
                >
                  <span className="font-mono text-[9px] font-bold uppercase text-[var(--gv-brand)]">
                    DAILY SUTRA QUOTE
                  </span>
                  <p className="text-xs font-bold italic text-[var(--gv-ink)]">&ldquo;{currentQuote.quote}&rdquo;</p>
                  <span className="block text-[10px] font-semibold text-[var(--gv-ink-muted)]">
                    — {currentQuote.author}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div
                    className={`rounded-xl border p-2.5 ${
                      isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-stone-200 bg-white'
                    }`}
                  >
                    <span className="block text-base font-black text-[var(--gv-brand)]">420</span>
                    <span className="text-[9px] font-bold uppercase text-[var(--gv-ink-muted)]">Karma Pts</span>
                  </div>
                  <div
                    className={`rounded-xl border p-2.5 ${
                      isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-stone-200 bg-white'
                    }`}
                  >
                    <span className="block text-base font-black text-emerald-600">7 Days</span>
                    <span className="text-[9px] font-bold uppercase text-[var(--gv-ink-muted)]">Streak</span>
                  </div>
                  <div
                    className={`rounded-xl border p-2.5 ${
                      isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-stone-200 bg-white'
                    }`}
                  >
                    <span className="block text-base font-black text-amber-600">8 Gathas</span>
                    <span className="text-[9px] font-bold uppercase text-[var(--gv-ink-muted)]">Learned</span>
                  </div>
                </div>

                {classes.length > 0 && (
                  <div
                    className={`space-y-2 rounded-2xl border p-3.5 ${
                      isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-stone-200 bg-white shadow-xs'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <Badge variant="success">NEXT LIVE CLASS</Badge>
                      <span className="text-[10px] font-bold text-[var(--gv-ink-muted)]">
                        {classes[0].dateTime.split(' ')[1]}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-[var(--gv-ink)]">{classes[0].topic}</h4>
                      <p className="text-[10px] text-[var(--gv-ink-muted)]">Guruji: {classes[0].guruji}</p>
                    </div>
                    <Button
                      className="w-full"
                      size="sm"
                      leftIcon={Play}
                      onClick={() => addToast('info', 'Live Class Launch', 'Simulating video class joining.')}
                    >
                      Join Class Room
                    </Button>
                  </div>
                )}
              </>
            )}

            {activeTab === 'sutras' && (
              <div className="space-y-3">
                <h3 className="text-sm font-black text-[var(--gv-ink)]">Sutras & Recitations</h3>
                <div className="space-y-2">
                  {sutras.slice(0, 4).map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between rounded-xl border p-3 ${
                        isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-stone-200 bg-white shadow-xs'
                      }`}
                    >
                      <div>
                        <span className="font-mono text-[9px] font-bold text-[var(--gv-brand)]">
                          SEQ #{s.sequenceNo}
                        </span>
                        <h4 className="text-xs font-bold text-[var(--gv-ink)]">{s.titleOriginal}</h4>
                        <p className="text-[10px] text-[var(--gv-ink-muted)]">
                          {s.titleEnglish} ({s.gathasCount} Gathas)
                        </p>
                      </div>
                      <Tooltip content="Play pronunciation">
                        <Button
                          variant="secondary"
                          size="icon"
                          aria-label={`Play ${s.titleEnglish}`}
                          onClick={() =>
                            addToast('info', 'Audio Audio Player', `Playing pronunciation for ${s.titleEnglish}`)
                          }
                        >
                          <Volume2 className="h-4 w-4" />
                        </Button>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'niyam' && (
              <div className="space-y-3">
                <h3 className="text-sm font-black text-[var(--gv-ink)]">Daily Niyam Checklist</h3>
                <div className="space-y-2">
                  {Object.entries(checkedNiyams).map(([name, isDone]) => (
                    <div
                      key={name}
                      onClick={() => toggleNiyam(name)}
                      className={`flex cursor-pointer items-center justify-between rounded-xl border p-3 transition-all ${
                        isDone
                          ? 'border-emerald-200 bg-emerald-50/80 text-emerald-900'
                          : isDarkMode
                          ? 'border-slate-800 bg-slate-900'
                          : 'border-stone-200 bg-white'
                      }`}
                    >
                      <span className="text-xs font-bold">{name}</span>
                      <CheckCircle2
                        className={`h-4 w-4 ${isDone ? 'fill-emerald-100 text-emerald-600' : 'text-slate-300'}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            className={`grid shrink-0 grid-cols-3 border-t p-2 text-center ${
              isDarkMode ? 'border-slate-800 bg-slate-900 text-slate-400' : 'border-stone-200 bg-white text-[var(--gv-ink-muted)]'
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveTab('home')}
              className={`rounded-lg py-1 text-[10px] font-bold ${
                activeTab === 'home' ? 'font-black text-[var(--gv-brand)]' : ''
              }`}
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('sutras')}
              className={`rounded-lg py-1 text-[10px] font-bold ${
                activeTab === 'sutras' ? 'font-black text-[var(--gv-brand)]' : ''
              }`}
            >
              Sutras
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('niyam')}
              className={`rounded-lg py-1 text-[10px] font-bold ${
                activeTab === 'niyam' ? 'font-black text-[var(--gv-brand)]' : ''
              }`}
            >
              Niyams
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
